import { Alert, MenuItem } from '@mui/material';
import { useFormikContext } from 'formik';
import { isEqual } from 'lodash';
import { useMemo } from 'react';

import { ContentV1, MarketplaceV1 } from '@jebel/constants';

import { FormMediaUploader, FormMoneyCents } from 'shared/components/form';
import { FormSelect, FormTextField } from 'shared/components/ui';

import { Container } from './CommunityProductForm.styles';

interface CommunityProductBasicFormValues {
  name: string;
  type: MarketplaceV1.CommunityProductType;
  description?: string;
  images: ContentV1.Media[];
  priceCents: number;
  color: string;
}

export interface CommunitySubscriptionFormValues extends CommunityProductBasicFormValues {
  type: MarketplaceV1.CommunityProductType.Subscription;
  frequency?: MarketplaceV1.CommunitySubscriptionFrequency;
}

export type CommunityProductFormValues = CommunitySubscriptionFormValues;

export const DEFAULT_COMMUNITY_PRODUCT_FORM_VALUES: CommunityProductFormValues = {
  name: '',
  description: '',
  images: [],
  type: MarketplaceV1.CommunityProductType.Subscription,
  priceCents: 0,
  frequency: MarketplaceV1.CommunitySubscriptionFrequency.OneTime,
  color: '#000000',
};

export interface CommunityProductFormProps {
  /**
   * Whether to show a warning about price changes affecting only new subscriptions.
   */
  withPriceChangeWarning?: boolean;
}

export function CommunityProductForm({
  withPriceChangeWarning = false,
}: CommunityProductFormProps) {
  const { values, initialValues: initials } = useFormikContext<CommunityProductFormValues>();

  const showPriceChangeWarning = useMemo(() => {
    const isSubscription = values.type === MarketplaceV1.CommunityProductType.Subscription;

    const isPriceChanged = !isEqual(values.priceCents, initials.priceCents);
    const isFrequencyChanged = !isEqual(values.frequency, initials.frequency);

    return withPriceChangeWarning && isSubscription && (isPriceChanged || isFrequencyChanged);
  }, [
    withPriceChangeWarning,
    values.priceCents,
    values.frequency,
    initials.priceCents,
    initials.frequency,
  ]);

  return (
    <Container>
      <FormTextField name="name" label="Membership Name" required />

      <FormTextField
        name="description"
        label="Membership Description"
        multiline
        rows={3}
        rowsMax={12}
      />

      <FormMediaUploader
        name="images"
        placeholder="Upload membership logo"
        helperText="Allowed formats: PNG, JPEG, SVG, WEBP. Max. Size: 500KB. Recommended size: 200x200px"
        accept="image/png, image/jpeg, image/svg+xml, image/webp"
        maxFiles={1}
      />

      <FormMoneyCents name="priceCents" label="Price" required />

      {values.type === MarketplaceV1.CommunityProductType.Subscription && (
        <FormSelect name="frequency" label="Frequency" required>
          <MenuItem value={MarketplaceV1.CommunitySubscriptionFrequency.OneTime}>One-time</MenuItem>
          <MenuItem value={MarketplaceV1.CommunitySubscriptionFrequency.Annual}>Annually</MenuItem>
        </FormSelect>
      )}

      {showPriceChangeWarning && (
        <Alert severity="warning">
          Changing the price/frequency of a subscription will affect <b>ONLY NEW SUBSCRIPTIONS</b>
          &nbsp; acquired after the change. Existing subscriptions will not be affected.
        </Alert>
      )}

      {/* Membership-specific fields */}

      <FormTextField
        type="color"
        label="Membership Color"
        variant="outlined"
        name="color"
        required
      />
    </Container>
  );
}
